import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { KModal } from '@/ui-library/components/molecules/KModal';
import { KText } from '@/ui-library/components/text';
import {
  notifyAdminCredits,
  CREDIT_MANAGEMENT_PATH,
} from './backgroundCheckApi';
import { notifyCoordinatorCredits } from '@/api/ai-coordinator/coordinator-credits';
import { useShowToast } from '@/hooks';
import { useKFlags } from '@/hooks/use-k-flags';

// One modal, two features. Each variant swaps (a) the copy, (b) which
// manage-permission field decides recruiter-vs-admin, and (c) the notify
// endpoint hit by "Notify admin".
type InsufficientCreditsVariant = 'cbv' | 'coordinator';

const VARIANT_CONFIG: Record<
  InsufficientCreditsVariant,
  {
    notify: () => Promise<void>;
    hasManagePermission: (_capError: any) => boolean;
    body: string;
  }
> = {
  cbv: {
    notify: notifyAdminCredits,
    hasManagePermission: (capError) =>
      !!capError?.user_has_manage_org_fraud_detector,
    body: 'You don’t have enough credits to review this candidate with fraud detector',
  },
  coordinator: {
    notify: notifyCoordinatorCredits,
    // The 402 payload uses user_has_manage_org_coordinator; the bulk
    // validate_bulk pre-check uses user_has_manage_credits — accept either.
    hasManagePermission: (capError) =>
      !!(
        capError?.user_has_manage_org_coordinator ??
        capError?.user_has_manage_credits
      ),
    body: 'You don’t have enough credits to run the AI coordinator',
  },
};

export const InsufficientCreditsModal = ({
  isOpen,
  onClose,
  capError,
  variant = 'cbv',
}: {
  isOpen: boolean;
  onClose: () => void;
  capError: any;
  variant?: InsufficientCreditsVariant;
}) => {
  const config = VARIANT_CONFIG[variant];
  const [isNotifying, setIsNotifying] = useState(false);
  const toast = useShowToast();
  const history = useHistory();
  const { tUnifiedCreditManagement } = useKFlags();
  const canNotifyAdmin = !config.hasManagePermission(capError);
  const reminderSent = capError?.reminder_sent_recently;

  // Admins go to credit management to top up only once unified credit
  // management is live; otherwise fall back to the Pylon support chat.
  const adminCtaLabel = tUnifiedCreditManagement
    ? 'Top up credits'
    : 'Contact support';
  const handleAdminCta = () => {
    if (tUnifiedCreditManagement) {
      history.push(CREDIT_MANAGEMENT_PATH);
    } else {
      (window as unknown as { Pylon?: (_cmd: string) => void }).Pylon?.('show');
    }
    onClose();
  };

  const handleNotifyAdmin = async () => {
    setIsNotifying(true);
    try {
      await config.notify();
      toast({ title: 'Admin has been notified about insufficient credits' });
      onClose();
    } catch {
      toast({ title: 'Failed to notify admin', status: 'error' });
    } finally {
      setIsNotifying(false);
    }
  };

  return (
    <KModal
      isOpen={isOpen}
      onClose={onClose}
      title="Insufficient credits"
      ctaLabel={
        canNotifyAdmin
          ? reminderSent
            ? 'Admin notified'
            : 'Notify admin'
          : adminCtaLabel
      }
      hideCloseButton={true}
      ctaAction={canNotifyAdmin ? handleNotifyAdmin : handleAdminCta}
      isCtaLoading={isNotifying}
      ctaButtonProps={canNotifyAdmin ? { isDisabled: reminderSent } : {}}
      enableCancelBtn={canNotifyAdmin}
      modalContentProps={{ borderRadius: '4px' }}
      modalFooterProps={{
        borderTop: '1px solid',
        borderColor: 'secondary-200',
        pt: '12px',
      }}
    >
      <KText color="secondary-700" mb="12px">
        {config.body}
        {!canNotifyAdmin &&
          (tUnifiedCreditManagement
            ? '. Top up to add more credits.'
            : '. Contact support for more credits.')}
      </KText>
    </KModal>
  );
};
